import { canInsertNode, mergeAttributes, Node } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";
import {
    formatVariableLabel,
    parseVariableTokens,
    serialiseVariableToken,
    type IVariableToken,
} from "../variable-token";
import { VariableMentionView } from "./variable-mention.view";
import {
    VARIABLE_MENTION_DATA_ATTRIBUTE,
    VARIABLE_MENTION_NODE_NAME,
    VARIABLE_MENTION_TOKEN_ATTRIBUTE,
} from "./variable-mention.constants";
import type { IVariableMentionOptions } from "./variable-mention.types";
import { variableTokenFromNode, variableTokenToAttributes } from "./variable-mention.utilities";

/**
 * Tiptap node for an Ask Spotlight variable: an inline pill that stands for one `IVariableToken`. It renders
 * as a `VariablePill`, serialises to the variable token (`{{type[#subtype]:(key=v[,v…][;…])|title|label[|label…]}}`)
 * and round-trips through HTML on a single `data-token` attribute. Insertion is by command only; the `@` dropdown is GAP-1849.
 */
export const VariableMention = Node.create<IVariableMentionOptions>({
    // One indivisible unit inside a line of text: selectable as a whole, never entered or dragged.
    name: VARIABLE_MENTION_NODE_NAME,
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,
    draggable: false,

    // No bold or italic on a pill. ProseMirror does not enforce this on its own, so the plugin below does.
    marks: "",

    addOptions() {
        return {
            getIcon: undefined,
            getTooltipPrefix: undefined,
            HTMLAttributes: {},
        };
    },

    // The five fields of an `IVariableToken`. None is written to or read from HTML on its own: `data-token`
    // carries them all, so a pasted element cannot override one field and keep the rest.
    addAttributes() {
        const tokenOnly = { renderHTML: () => ({}), parseHTML: () => null };

        return {
            type: { default: null, ...tokenOnly },
            subtype: { default: null, ...tokenOnly },
            values: { default: {}, ...tokenOnly },
            title: { default: null, ...tokenOnly },
            labels: { default: [], ...tokenOnly },
        };
    },

    // HTML in: a `<span data-variable-mention data-token="…">` becomes a pill when its token parses as exactly
    // one variable. Anything else is left to the other rules, which treat it as text.
    parseHTML() {
        return [
            {
                tag: `span[${VARIABLE_MENTION_DATA_ATTRIBUTE}]`,
                getAttrs: (element) => {
                    const token =
                        typeof element === "string" ? null : element.getAttribute(VARIABLE_MENTION_TOKEN_ATTRIBUTE);

                    if (token === null) {
                        return false;
                    }

                    const segments = parseVariableTokens(token);

                    if (segments.length !== 1 || segments[0].type !== "variable") {
                        return false;
                    }

                    return variableTokenToAttributes(segments[0].variable);
                },
            },
        ];
    },

    // HTML out: the same span, with the pill text as its text.
    renderHTML({ node, HTMLAttributes }) {
        const variable = variableTokenFromNode(node);

        return [
            "span",
            mergeAttributes({ [VARIABLE_MENTION_DATA_ATTRIBUTE]: "" }, this.options.HTMLAttributes, HTMLAttributes, {
                [VARIABLE_MENTION_TOKEN_ATTRIBUTE]: serialiseVariableToken(variable),
            }),
            formatVariableLabel(variable),
        ];
    },

    // Plain text out: `editor.getText()` gives the token, which is what the agent receives.
    renderText({ node }) {
        return serialiseVariableToken(variableTokenFromNode(node));
    },

    // ProseMirror owns the editor DOM, so a React component draws the pill through a node view.
    addNodeView() {
        return ReactNodeViewRenderer(VariableMentionView);
    },

    addCommands() {
        return {
            insertVariableMention:
                (variable: IVariableToken) =>
                ({ commands, state }) => {
                    // Refuse an invalid variable before it reaches the document, where it would break rendering.
                    try {
                        serialiseVariableToken(variable);
                    } catch {
                        return false;
                    }

                    // `insertContent` reports success even where the schema has no room, e.g. inside a code block.
                    if (!canInsertNode(state, state.schema.nodes[this.name])) {
                        return false;
                    }

                    // Insert the pill plus one space, reusing a space that already follows the caret.
                    const { from, to } = state.selection;
                    const nodeAfter = state.doc.resolve(to).nodeAfter;
                    const followedBySpace = nodeAfter?.isText === true && nodeAfter.text?.startsWith(" ") === true;

                    return commands.insertContentAt({ from, to: followedBySpace ? to + 1 : to }, [
                        { type: this.name, attrs: variableTokenToAttributes(variable) },
                        { type: "text", text: " " },
                    ]);
                },
        };
    },

    // Marks are checked against the parent paragraph, not the pill, so bolding a selection marks the pill too.
    // After each change, take any mark back off every pill. Appended transactions share the user's undo step.
    addProseMirrorPlugins() {
        const nodeName = this.name;

        return [
            new Plugin({
                key: new PluginKey(`${nodeName}MarkGuard`),
                appendTransaction: (transactions, _oldState, newState) => {
                    if (!transactions.some((transaction) => transaction.docChanged)) {
                        return null;
                    }

                    const { tr } = newState;

                    newState.doc.descendants((node, position) => {
                        if (node.type.name !== nodeName) {
                            return;
                        }

                        node.marks.forEach((mark) => tr.removeMark(position, position + node.nodeSize, mark.type));
                    });

                    return tr.steps.length === 0 ? null : tr;
                },
            }),
        ];
    },

    // Two-step delete: with the caret right after a pill, the first Backspace selects it. The second press
    // falls through to Tiptap's default delete, which keeps the deletion in the history for undo.
    addKeyboardShortcuts() {
        return {
            Backspace: () => {
                const { selection } = this.editor.state;
                const nodeBefore = selection.$from.nodeBefore;

                if (!selection.empty || nodeBefore?.type.name !== this.name) {
                    return false;
                }

                return this.editor.commands.setNodeSelection(selection.$from.pos - nodeBefore.nodeSize);
            },
        };
    },
});
